import { useState, useCallback, useRef, useEffect, useId } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { useAuth } from "@/contexts/AuthContext";
import { ConditionRuleEditor } from "@/components/ConditionRuleEditor";
import type { ConditionRule } from "@/components/ConditionRuleEditor";
import {
  Type, AlignLeft, Hash, DollarSign, Phone, Mail, Link, Lock, KeyRound,
  List, Calendar, CheckSquare, FileText, ToggleLeft, PenLine, MapPin, Landmark, UserRound,
  Upload, SlidersHorizontal, Rows3, GripVertical, Trash2, Eye, EyeOff,
  Plus, Settings2, ChevronLeft, Loader2, Columns, CheckCircle2,
} from "lucide-react";
import type { GenericCategory, GenericSection, GenericField } from "@shared/schema";

// ── Field type palette definitions ──────────────────────────────────────────

const PALETTE_TYPES = [
  { value: "text",             label: "Text",           icon: Type,              description: "Single-line text" },
  { value: "multiline",        label: "Multiline",      icon: AlignLeft,         description: "Multi-line text area" },
  { value: "number",           label: "Number",         icon: Hash,              description: "Numeric input" },
  { value: "currency",         label: "Currency",       icon: DollarSign,        description: "Monetary amount" },
  { value: "phone",            label: "Phone",          icon: Phone,             description: "Phone number" },
  { value: "email",            label: "Email",          icon: Mail,              description: "Email address" },
  { value: "url",              label: "URL",            icon: Link,              description: "Web address" },
  { value: "password",         label: "Password",       icon: Lock,              description: "Masked password" },
  { value: "pin",              label: "PIN",            icon: KeyRound,          description: "Short numeric code" },
  { value: "select",           label: "Dropdown",       icon: List,              description: "Select from options" },
  { value: "date",             label: "Date",           icon: Calendar,          description: "Date picker" },
  { value: "checkbox",         label: "Checkbox",       icon: CheckSquare,       description: "Yes / No toggle" },
  { value: "file_ref",         label: "File",           icon: FileText,          description: "Document attachment" },
  { value: "notes",            label: "Notes",          icon: AlignLeft,         description: "Notes area" },
  { value: "yes_no",           label: "Yes / No",       icon: ToggleLeft,        description: "Boolean switch" },
  { value: "signature",        label: "Signature",      icon: PenLine,           description: "Drawable signature pad" },
  { value: "address",          label: "Address",        icon: MapPin,            description: "Structured address" },
  { value: "institution",      label: "Institution (type-ahead)", icon: Landmark,  description: "Matches a known institution; free text always accepted" },
  { value: "contact",          label: "Contact (type-ahead)",     icon: UserRound, description: "Matches a person or organisation already in the plan; free text always accepted" },
  { value: "file_upload",      label: "File Upload",    icon: Upload,            description: "Inline file attach" },
  { value: "slider",           label: "Slider",         icon: SlidersHorizontal, description: "Numeric range slider" },
  { value: "repeating_group",  label: "Repeating",      icon: Rows3,             description: "Repeatable row group" },
] as const;

type FieldTypeValue = typeof PALETTE_TYPES[number]["value"];

// ── Types ────────────────────────────────────────────────────────────────────

interface BuilderField extends GenericField {
  _saving?: boolean;
}

type Mode = "admin" | "advisor";

interface FormBuilderProps {
  /** Present when launched in advisor context */
  clientId?: string;
}

// ── Palette item (draggable) ─────────────────────────────────────────────────

function PaletteItem({ type }: { type: typeof PALETTE_TYPES[number] }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette::${type.value}`,
    data: { origin: "palette", fieldType: type.value },
  });
  const Icon = type.icon;
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`flex items-center gap-2 px-3 py-2 rounded-md border bg-background cursor-grab active:cursor-grabbing select-none transition-opacity ${isDragging ? "opacity-40" : "hover:bg-muted/50"}`}
      data-testid={`palette-${type.value}`}
    >
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <p className="text-xs font-medium leading-tight">{type.label}</p>
        <p className="text-[10px] text-muted-foreground leading-tight truncate">{type.description}</p>
      </div>
    </div>
  );
}

// Palette ghost shown by DragOverlay
function PaletteGhost({ fieldType }: { fieldType: string }) {
  const t = PALETTE_TYPES.find(p => p.value === fieldType);
  if (!t) return null;
  const Icon = t.icon;
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-background shadow-lg opacity-90">
      <Icon className="h-3.5 w-3.5 text-primary shrink-0" />
      <p className="text-xs font-medium">{t.label}</p>
    </div>
  );
}

// ── Canvas field (sortable) ──────────────────────────────────────────────────

function CanvasField({
  field,
  isSelected,
  isReadOnly,
  canDelete,
  onSelect,
  onDelete,
}: {
  field: BuilderField;
  isSelected: boolean;
  isReadOnly: boolean;
  /** s220 Lane G: template fields are never deletable here; client custom fields are. */
  canDelete: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: field.id, data: { origin: "canvas" } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const t = PALETTE_TYPES.find(p => p.value === field.fieldType);
  const Icon = t?.icon ?? Type;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg border bg-background transition-colors cursor-pointer ${
        isSelected ? "border-primary ring-1 ring-primary bg-primary/5" : "hover:border-muted-foreground/40"
      } ${isReadOnly ? "opacity-70" : ""}`}
      onClick={onSelect}
      data-testid={`canvas-field-${field.id}`}
    >
      {/* Drag handle */}
      {!isReadOnly && (
        <div
          {...attributes}
          {...listeners}
          onClick={e => e.stopPropagation()}
          className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground"
        >
          <GripVertical className="h-4 w-4" />
        </div>
      )}

      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{field.name}</p>
        <p className="text-xs text-muted-foreground">{t?.label ?? field.fieldType}</p>
      </div>

      <div className="flex items-center gap-1.5 shrink-0">
        {field.isCritical && <Badge variant="destructive" className="text-[10px] py-0 px-1">Critical</Badge>}
        {field.isRequired && <Badge variant="secondary" className="text-[10px] py-0 px-1">Required</Badge>}
        {(field as any).layoutWidth === 'half' && (
          <Badge variant="outline" className="text-[10px] py-0 px-1 gap-0.5">
            <Columns className="h-2.5 w-2.5" />Half
          </Badge>
        )}
        {field.conditions && <Badge variant="outline" className="text-[10px] py-0 px-1">Conditional</Badge>}
        {isReadOnly && <Lock className="h-3 w-3 text-muted-foreground/60" />}
        {canDelete && (
          <Button aria-label="Delete field"
            variant="ghost" size="icon"
            className="h-6 w-6 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
            onClick={e => { e.stopPropagation(); onDelete(); }}
            data-testid={`delete-field-${field.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Canvas drop zone
function CanvasDropZone({ children, isEmpty }: { children: React.ReactNode; isEmpty: boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id: "canvas-drop-zone" });
  return (
    <div
      ref={setNodeRef}
      className={`flex-1 min-h-[300px] rounded-lg border-2 border-dashed transition-colors ${
        isOver ? "border-primary bg-primary/5" : isEmpty ? "border-muted-foreground/20" : "border-transparent"
      }`}
    >
      {isEmpty ? (
        <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-16">
          <Settings2 className="h-10 w-10 mb-3 opacity-30" />
          <p className="text-sm font-medium">Drag fields from the palette</p>
          <p className="text-xs mt-1">or click a type to add it</p>
        </div>
      ) : children}
    </div>
  );
}

// ── Settings panel ───────────────────────────────────────────────────────────

export function SettingsPanel({
  field,
  allSectionFields,
  mode,
  clientId,
  sectionId,
  onSaved,
}: {
  field: BuilderField;
  allSectionFields: BuilderField[];
  mode: Mode;
  clientId?: string;
  sectionId: string;
  onSaved: (updated: GenericField) => void;
}) {
  const uid = useId();
  const { toast } = useToast();
  const [savedFlash, setSavedFlash] = useState(false);
  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current); }, []);

  const { data: governmentForms = [] } = useQuery<Array<{ id: string; title: string; agency: string }>>({
    queryKey: ["/api/admin/government-forms"],
    staleTime: Infinity,
  });

  const saveMutation = useMutation({
    mutationFn: async (patch: Partial<GenericField> & { options?: unknown; conditions?: unknown; layoutWidth?: string | null; formTags?: string[] | null }) => {
      if (mode === "admin") {
        const res = await apiRequest("PATCH", `/api/admin/field-config/fields/${field.id}`, patch);
        return res.json() as Promise<GenericField>;
      } else {
        const res = await apiRequest("PATCH", `/api/clients/${clientId}/custom-fields/${field.id}`, patch);
        return res.json() as Promise<GenericField>;
      }
    },
    onSuccess: (updated) => {
      onSaved(updated);
      setSavedFlash(true);
      if (savedTimer.current) clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSavedFlash(false), 2000);
      if (mode === "admin") {
        queryClient.invalidateQueries({ queryKey: ["/api/admin/field-config/sections", sectionId, "fields"] });
      }
    },
    onError: (err: Error) => toast({ title: "Save failed", description: err.message, variant: "destructive" }),
  });

  const save = useCallback((patch: Record<string, unknown>) => saveMutation.mutate(patch), [saveMutation]);

  const opts = (field.options ?? {}) as Record<string, unknown>;
  const choices: string[] = (opts.choices as string[]) ?? [];
  const subFields: Array<{ key: string; label: string; type: string }> = (opts.subFields as any[]) ?? [];

  const layoutWidth: "half" | "full" = ((field as any).layoutWidth as "half" | "full") ?? "full";

  return (
    <div className="space-y-5 text-sm">
      {/* Header with save indicator */}
      <div className="flex items-center gap-2">
        {(() => { const t = PALETTE_TYPES.find(p => p.value === field.fieldType); const Icon = t?.icon ?? Type; return <Icon className="h-4 w-4 text-muted-foreground" />; })()}
        <h3 className="font-semibold truncate">{field.name}</h3>
        <div className="ml-auto flex items-center gap-1 shrink-0">
          {saveMutation.isPending && (
            <>
              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
              <span className="text-[10px] text-muted-foreground">Saving…</span>
            </>
          )}
          {savedFlash && !saveMutation.isPending && (
            <>
              <CheckCircle2 className="h-3 w-3" style={{ color: '#1ABC9C' }} />
              <span className="text-[10px] font-medium" style={{ color: '#1ABC9C' }}>Saved</span>
            </>
          )}
        </div>
      </div>

      <Separator />

      {/* Label */}
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide" htmlFor={`${uid}-field-name`}>Label</Label>
        <Input id={`${uid}-field-name`}
          defaultValue={field.name}
          key={`name-${field.id}`}
          onBlur={e => { if (e.target.value.trim() && e.target.value !== field.name) save({ name: e.target.value.trim() }); }}
          className="h-8 text-sm"
          data-testid="settings-name"
        />
      </div>

      {/* Placeholder */}
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide" htmlFor={`${uid}-field-placeholder`}>Placeholder</Label>
        <Input id={`${uid}-field-placeholder`}
          defaultValue={field.placeholder ?? ""}
          key={`ph-${field.id}`}
          onBlur={e => save({ placeholder: e.target.value })}
          className="h-8 text-sm"
          placeholder="e.g. Enter your answer…"
          data-testid="settings-placeholder"
        />
      </div>

      {/* Required / Critical toggles */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-normal" htmlFor={`${uid}-field-required`}>Required</Label>
          <Switch id={`${uid}-field-required`}
            checked={!!field.isRequired}
            onCheckedChange={v => save({ isRequired: v })}
            data-testid="settings-required"
          />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-xs font-normal" htmlFor={`${uid}-field-critical`}>Critical</Label>
          <Switch id={`${uid}-field-critical`}
            checked={!!field.isCritical}
            onCheckedChange={v => save({ isCritical: v })}
            data-testid="settings-critical"
          />
        </div>
      </div>

      <Separator />

      {/* Layout width */}
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Layout Width</Label>
        <div className="grid grid-cols-2 gap-2">
          {([
            { value: "half" as const, label: "Half width", desc: "2-column grid" },
            { value: "full" as const, label: "Full width", desc: "Full span" },
          ]).map(opt => {
            const isSelected = layoutWidth === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => save({ layoutWidth: opt.value })}
                className={`flex flex-col items-center gap-1 p-2 rounded-lg border text-center transition-colors ${
                  isSelected ? "border-primary bg-primary/5" : "hover:bg-muted/30 border-border"
                }`}
                data-testid={`settings-layout-${opt.value}`}
              >
                <Columns className={`h-3.5 w-3.5 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                <span className={`text-[11px] font-medium leading-tight ${isSelected ? "text-primary" : ""}`}>{opt.label}</span>
                <span className="text-[10px] text-muted-foreground">{opt.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      <Separator />

      {/* Type-specific options */}
      {field.fieldType === "select" && (
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground uppercase tracking-wide" htmlFor={`${uid}-dropdown-options`}>Dropdown Options</Label>
          <Textarea id={`${uid}-dropdown-options`}
            defaultValue={choices.join("\n")}
            key={`choices-${field.id}`}
            rows={4}
            placeholder="One option per line"
            className="text-sm"
            onBlur={e => save({ options: { ...opts, choices: e.target.value.split("\n").filter(c => c.trim()) } })}
            data-testid="settings-choices"
          />
          <p className="text-[10px] text-muted-foreground">Enter one option per line</p>
        </div>
      )}

      {field.fieldType === "slider" && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wide">Slider Range</Label>
          <div className="grid grid-cols-3 gap-2">
            {(["min", "max", "step"] as const).map(k => (
              <div key={k} className="space-y-1">
                <Label className="text-[10px] text-muted-foreground capitalize" htmlFor={`${uid}-slider-${k}`}>{k}</Label>
                <Input id={`${uid}-slider-${k}`}
                  type="number"
                  defaultValue={String(opts[k] ?? (k === "max" ? 100 : k === "step" ? 1 : 0))}
                  key={`${k}-${field.id}`}
                  className="h-7 text-xs"
                  onBlur={e => save({ options: { ...opts, [k]: Number(e.target.value) } })}
                  data-testid={`settings-${k}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {field.fieldType === "repeating_group" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Sub-fields</Label>
            <Button
              type="button" variant="outline" size="sm" className="h-6 text-xs px-2"
              onClick={() => save({ options: { ...opts, subFields: [...subFields, { key: "", label: "New Field", type: "text" }] } })}
            >
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
          <div className="space-y-1.5">
            {subFields.map((sf, idx) => (
              <div key={idx} className="flex gap-1 items-center">
                <Input
                  className="h-7 text-xs flex-1"
                  defaultValue={sf.label}
                  key={`sf-label-${field.id}-${idx}`}
                  placeholder="Label"
                  onBlur={e => {
                    const updated = [...subFields];
                    updated[idx] = { ...updated[idx], label: e.target.value, key: e.target.value.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "") };
                    save({ options: { ...opts, subFields: updated } });
                  }}
                />
                <select
                  className="h-7 rounded border border-input bg-background px-1 text-xs"
                  defaultValue={sf.type}
                  key={`sf-type-${field.id}-${idx}`}
                  onChange={e => {
                    const updated = [...subFields]; updated[idx] = { ...updated[idx], type: e.target.value };
                    save({ options: { ...opts, subFields: updated } });
                  }}
                >
                  {["text","number","currency","date","email","phone"].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <Button aria-label="Delete sub-field" variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  onClick={() => save({ options: { ...opts, subFields: subFields.filter((_, i) => i !== idx) } })}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
            {subFields.length === 0 && <p className="text-[10px] text-muted-foreground">No sub-fields. Add one above.</p>}
          </div>
        </div>
      )}

      <Separator />

      {/* Government Form Tags */}
      {mode === "admin" && governmentForms.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wide">Government Forms</Label>
          <div className="space-y-1.5">
            {governmentForms.map(form => {
              const currentTags: string[] = ((field as any).formTags as string[]) ?? [];
              const checked = currentTags.includes(form.id);
              return (
                <label key={form.id} className="flex items-start gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    className="mt-0.5 accent-primary"
                    checked={checked}
                    onChange={e => {
                      const next = e.target.checked
                        ? [...currentTags, form.id]
                        : currentTags.filter(t => t !== form.id);
                      save({ formTags: next });
                    }}
                  />
                  <span className="text-xs leading-tight group-hover:text-foreground text-muted-foreground">
                    {form.title}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      <Separator />

      {/* Conditional logic */}
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground uppercase tracking-wide">Conditional Logic</Label>
        <ConditionRuleEditor
          conditions={(field.conditions as ConditionRule | null) ?? null}
          onChange={conditions => save({ conditions })}
          availableFields={allSectionFields.filter(f => f.id !== field.id).map(f => ({
            id: f.id,
            name: f.name,
            fieldType: f.fieldType,
            options: f.options as { choices?: string[] } | null,
          }))}
        />
      </div>
    </div>
  );
}

// ── Field preview ────────────────────────────────────────────────────────────

function FieldPreview({ field }: { field: GenericField }) {
  const uid = useId();
  const opts = (field.options ?? {}) as Record<string, unknown>;
  const ph = field.placeholder || `Enter ${field.name.toLowerCase()}`;
  const t = PALETTE_TYPES.find(p => p.value === field.fieldType);

  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide" htmlFor={`${uid}-preview`}>
        {field.name}{field.isRequired && <span className="text-destructive ml-1">*</span>}
      </Label>
      {(field.fieldType === "multiline" || field.fieldType === "notes") ? (
        <Textarea id={`${uid}-preview`} placeholder={ph} disabled className="resize-none min-h-[72px] text-sm" />
      ) : field.fieldType === "checkbox" || field.fieldType === "yes_no" ? (
        <div className="flex items-center gap-2 pt-1">
          <Switch disabled />
          <span className="text-sm text-muted-foreground">No</span>
        </div>
      ) : field.fieldType === "select" ? (
        <Select disabled>
          <SelectTrigger className="text-sm h-9"><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>{((opts.choices as string[]) ?? []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      ) : field.fieldType === "date" ? (
        <Input type="date" disabled className="text-sm" />
      ) : field.fieldType === "slider" ? (
        <div className="flex items-center gap-3 pt-1">
          <input type="range" min={Number(opts.min ?? 0)} max={Number(opts.max ?? 100)} step={Number(opts.step ?? 1)} disabled className="flex-1" />
          <span className="text-xs text-muted-foreground w-12 text-right">{String(opts.min ?? 0)}</span>
        </div>
      ) : field.fieldType === "address" ? (
        <div className="space-y-1.5">
          {["Street", "City", "Province / State", "Postal Code"].map(lbl => (
            <Input key={lbl} placeholder={lbl} disabled className="text-sm h-8" />
          ))}
        </div>
      ) : field.fieldType === "institution" ? (
        <Input placeholder="Start typing…" disabled className="text-sm h-8" />
      ) : field.fieldType === "signature" ? (
        <div className="h-20 rounded-md border-2 border-dashed flex items-center justify-center text-xs text-muted-foreground bg-muted/10">
          <PenLine className="h-4 w-4 mr-2" /> Sign here
        </div>
      ) : field.fieldType === "repeating_group" ? (
        <div className="rounded-md border p-2 text-xs text-muted-foreground">
          Repeating group — {((opts.subFields as any[]) ?? []).length || "no"} sub-fields
        </div>
      ) : (
        <Input placeholder={ph} disabled className="text-sm" />
      )}
    </div>
  );
}

// ── Main form builder canvas ─────────────────────────────────────────────────

function FormBuilderCanvas({
  sectionId,
  mode,
  clientId,
}: {
  sectionId: string;
  mode: Mode;
  clientId?: string;
}) {
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activePaletteType, setActivePaletteType] = useState<string | null>(null);
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  // ── Fetch fields ──
  const fieldsKey = mode === "admin"
    ? ["/api/admin/field-config/sections", sectionId, "fields"]
    : ["/api/form-builder/sections", sectionId, "fields", clientId];

  const { data: rawFields = [], isLoading } = useQuery<GenericField[]>({
    queryKey: fieldsKey,
    queryFn: async () => {
      if (mode === "admin") {
        const res = await fetch(`/api/admin/field-config/sections/${sectionId}/fields`);
        if (!res.ok) throw new Error("Failed to fetch fields");
        return res.json();
      } else {
        // Fetch template + custom fields for this section
        const [tRes, cRes] = await Promise.all([
          fetch(`/api/generic-sections/${sectionId}/fields`),
          fetch(`/api/clients/${clientId}/custom-fields?sectionId=${sectionId}`),
        ]);
        const template: GenericField[] = tRes.ok ? await tRes.json() : [];
        const custom: GenericField[] = cRes.ok ? await cRes.json() : [];
        // Merge, custom fields override template duplicates by id
        const customIds = new Set(custom.map(f => f.id));
        return [...template.filter(f => !customIds.has(f.id)), ...custom]
          .sort((a, b) => a.sortOrder - b.sortOrder);
      }
    },
    staleTime: 0,
  });

  const fields: BuilderField[] = rawFields;
  const orderedFields = localOrder
    ? localOrder.map(id => fields.find(f => f.id === id)).filter(Boolean) as BuilderField[]
    : [...fields].sort((a, b) => a.sortOrder - b.sortOrder);

  const selectedField = orderedFields.find(f => f.id === selectedId) ?? null;

  // ── Mutations ──
  const createMutation = useMutation({
    mutationFn: async ({ fieldType, name }: { fieldType: string; name: string }) => {
      if (mode === "admin") {
        const res = await apiRequest("POST", `/api/admin/field-config/sections/${sectionId}/fields`, {
          name, fieldType, fieldKey: `${fieldType}_${Date.now()}`,
        });
        return res.json() as Promise<GenericField>;
      } else {
        const res = await apiRequest("POST", `/api/clients/${clientId}/custom-fields/blank`, {
          name, sectionId, fieldType,
        });
        return res.json() as Promise<GenericField>;
      }
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: fieldsKey });
      setSelectedId(created.id);
    },
    onError: (err: Error) => toast({ title: "Failed to add field", description: err.message, variant: "destructive" }),
  });

  // s220 Lane G: admin mode has no delete. A template field's home is `shared/template-spec/` —
  // deleting the row cascaded away every client's cell and the reconciler re-minted the field on
  // the next boot, so the control could only lose data. Client custom fields are a different
  // subject and DO delete here; the trash icon is gated on `canDelete` below.
  const deleteMutation = useMutation({
    mutationFn: async (fieldId: string) => {
      await apiRequest("DELETE", `/api/clients/${clientId}/custom-fields/${fieldId}`);
    },
    onSuccess: (_, fieldId) => {
      queryClient.invalidateQueries({ queryKey: fieldsKey });
      if (selectedId === fieldId) setSelectedId(null);
    },
    onError: (err: Error) => toast({ title: "Failed to delete field", description: err.message, variant: "destructive" }),
  });

  const reorderMutation = useMutation({
    mutationFn: async (ordered: BuilderField[]) => {
      if (mode === "admin") {
        await apiRequest("POST", "/api/admin/field-config/fields/reorder", {
          sectionId,
          fieldIds: ordered.map(f => f.id),
        });
      } else {
        await apiRequest("PUT", `/api/clients/${clientId}/fields/reorder`, {
          updates: ordered.map((f, i) => ({ id: f.id, sortOrder: i })),
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: fieldsKey });
      setLocalOrder(null);
    },
    onError: (err: Error) => {
      setLocalOrder(null);
      toast({ title: "Reorder failed", description: err.message, variant: "destructive" });
    },
  });

  // ── DnD handlers ──
  const onDragStart = useCallback(({ active }: DragStartEvent) => {
    setActiveId(String(active.id));
    if (active.data.current?.origin === "palette") {
      setActivePaletteType(active.data.current.fieldType as string);
    }
  }, []);

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): each fire mints a field row.
  const addFieldFlight = useSingleFlight();

  const addField = useCallback((fieldType: string) => {
    const t = PALETTE_TYPES.find(p => p.value === fieldType);
    addFieldFlight.runQuiet(() => createMutation.mutateAsync({ fieldType, name: t?.label ?? fieldType }));
  }, [createMutation, addFieldFlight]);

  const onDragEnd = useCallback(({ active, over }: DragEndEvent) => {
    setActiveId(null);
    setActivePaletteType(null);

    if (!over) return;

    const origin = active.data.current?.origin;

    // Palette item dropped on canvas
    if (origin === "palette") {
      addField(active.data.current!.fieldType as string);
      return;
    }

    // Canvas field reorder
    if (origin === "canvas" && String(active.id) !== String(over.id)) {
      const oldIndex = orderedFields.findIndex(f => f.id === active.id);
      const newIndex = orderedFields.findIndex(f => f.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(orderedFields, oldIndex, newIndex);
      setLocalOrder(reordered.map(f => f.id));
      reorderMutation.mutate(reordered);
    }
  }, [orderedFields, addField, reorderMutation]);

  const handleFieldSaved = useCallback((updated: GenericField) => {
    queryClient.invalidateQueries({ queryKey: fieldsKey });
  }, [fieldsKey]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ── Preview mode ──
  if (preview) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-sm">Preview — client view</h3>
          <Button variant="outline" size="sm" onClick={() => setPreview(false)} className="gap-1.5">
            <EyeOff className="h-3.5 w-3.5" /> Exit Preview
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-xl mx-auto bg-card border rounded-lg shadow-sm p-6">
            {orderedFields.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No fields added yet.</p>
            ) : (
              <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                {orderedFields.map(field => {
                  const isHalf = (field as any).layoutWidth === 'half';
                  return (
                    <div key={field.id} className={isHalf ? '' : 'col-span-2'}>
                      <FieldPreview field={field} />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Builder mode ──
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div className="flex h-full overflow-hidden">
        {/* ── Left: palette ── */}
        <div className="w-52 shrink-0 border-r overflow-y-auto bg-muted/10">
          <div className="p-3 border-b" style={{ backgroundColor: '#2C3E50' }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.9)' }}>Field Types</p>
            <p className="text-[10px] mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>Drag or click to add</p>
          </div>
          <div className="p-2 space-y-1">
            {PALETTE_TYPES.map(t => (
              <div key={t.value} onClick={() => addField(t.value)}>
                <PaletteItem type={t} />
              </div>
            ))}
          </div>
        </div>

        {/* ── Centre: canvas ── */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderBottom: '2px solid #1ABC9C', backgroundColor: '#2C3E50' }}>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {orderedFields.length} field{orderedFields.length !== 1 ? "s" : ""}
              {reorderMutation.isPending && " · saving order…"}
            </p>
            <Button
              variant="outline" size="sm" onClick={() => setPreview(true)}
              className="gap-1.5 border-white/30 text-white hover:bg-white/10 hover:text-white"
              data-testid="button-preview"
            >
              <Eye className="h-3.5 w-3.5" /> Preview
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <SortableContext items={orderedFields.map(f => f.id)} strategy={verticalListSortingStrategy}>
              <CanvasDropZone isEmpty={orderedFields.length === 0}>
                <div className="space-y-2">
                  {orderedFields.map(field => {
                    const editable = mode === "admin" || (field as any).isCustom;
                    // s220 Lane G: template fields (admin mode) cannot be deleted from here —
                    // retire them in `shared/template-spec/` (isActive: false). Client custom
                    // fields still can be.
                    const canDelete = mode !== "admin" && (field as any).isCustom;
                    return (
                      <CanvasField
                        key={field.id}
                        field={field}
                        isSelected={selectedId === field.id}
                        isReadOnly={!editable}
                        canDelete={canDelete}
                        onSelect={() => setSelectedId(selectedId === field.id ? null : field.id)}
                        onDelete={() => deleteMutation.mutate(field.id)}
                      />
                    );
                  })}
                </div>
              </CanvasDropZone>
            </SortableContext>
          </div>
        </div>

        {/* ── Right: settings ── */}
        <div className="w-72 shrink-0 border-l overflow-y-auto bg-muted/5">
          <div className="px-4 py-2.5 border-b" style={{ backgroundColor: '#2C3E50', borderBottom: '2px solid #1ABC9C' }}>
            <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'rgba(255,255,255,0.9)' }}>Field Settings</p>
          </div>
          {selectedField ? (
            <div className="p-4">
              <SettingsPanel
                key={selectedField.id}
                field={selectedField}
                allSectionFields={orderedFields}
                mode={mode}
                clientId={clientId}
                sectionId={sectionId}
                onSaved={handleFieldSaved}
              />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground p-6 text-center">
              <Settings2 className="h-8 w-8 mb-3 opacity-30" />
              <p className="text-sm font-medium">Field Settings</p>
              <p className="text-xs mt-1">Click a field on the canvas to configure it</p>
            </div>
          )}
        </div>
      </div>

      {/* Drag overlay */}
      <DragOverlay>
        {activePaletteType && <PaletteGhost fieldType={activePaletteType} />}
        {activeId && !activePaletteType && (() => {
          const f = orderedFields.find(f => f.id === activeId);
          if (!f) return null;
          const t = PALETTE_TYPES.find(p => p.value === f.fieldType);
          const Icon = t?.icon ?? Type;
          return (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border bg-background shadow-xl">
              <GripVertical className="h-4 w-4 text-muted-foreground" />
              <Icon className="h-4 w-4 text-muted-foreground" />
              <p className="text-sm font-medium">{f.name}</p>
            </div>
          );
        })()}
      </DragOverlay>
    </DndContext>
  );
}

// ── Page wrapper ─────────────────────────────────────────────────────────────

export default function FormBuilderPage({ clientId }: FormBuilderProps) {
  const { user } = useAuth();
  const mode: Mode = clientId ? "advisor" : "admin";
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedSectionId, setSelectedSectionId] = useState("");

  // Category list
  const { data: categories = [] } = useQuery<GenericCategory[]>({
    queryKey: mode === "admin" ? ["/api/admin/field-config/categories"] : ["/api/unified/progress", clientId],
    queryFn: async () => {
      if (mode === "admin") {
        const res = await fetch("/api/admin/field-config/categories");
        return res.json();
      }
      const res = await fetch(`/api/unified/progress?clientId=${clientId}`);
      const data: any[] = await res.json();
      return data.map(d => ({ id: d.categoryId ?? d.id, name: d.name, slug: d.slug }));
    },
  });

  // Section list
  const { data: sections = [] } = useQuery<GenericSection[]>({
    queryKey: ["form-builder-sections", selectedCategoryId, mode],
    queryFn: async () => {
      if (!selectedCategoryId) return [];
      if (mode === "admin") {
        const res = await fetch(`/api/admin/field-config/categories/${selectedCategoryId}/sections`);
        return res.json();
      }
      const res = await fetch(`/api/generic-categories/${selectedCategoryId}/sections`);
      return res.json();
    },
    enabled: !!selectedCategoryId,
  });

  const handleCategoryChange = (id: string) => {
    setSelectedCategoryId(id);
    setSelectedSectionId("");
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-4 px-6 py-4 border-b shrink-0" style={{ backgroundColor: '#2C3E50', borderBottom: '2px solid #1ABC9C' }}>
        <div>
          <h1 className="text-lg font-semibold" style={{ color: '#fff' }}>Form Builder</h1>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {mode === "admin" ? "Edit template fields" : "Build custom fields for this client"}
          </p>
        </div>
        <div className="flex items-center gap-3 ml-auto">
          {/* Category selector */}
          <Select value={selectedCategoryId} onValueChange={handleCategoryChange}>
            <SelectTrigger className="h-8 w-[200px] text-sm bg-white/10 border-white/30 text-white hover:bg-white/15" data-testid="select-category">
              <SelectValue placeholder="Select category…" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((c: any) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Section selector */}
          <Select
            value={selectedSectionId}
            onValueChange={setSelectedSectionId}
            disabled={!selectedCategoryId}
          >
            <SelectTrigger className="h-8 w-[200px] text-sm bg-white/10 border-white/30 text-white hover:bg-white/15" data-testid="select-section">
              <SelectValue placeholder="Select section…" />
            </SelectTrigger>
            <SelectContent>
              {sections.map((s: any) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Canvas */}
      <div className="flex-1 overflow-hidden">
        {selectedSectionId ? (
          <FormBuilderCanvas
            key={selectedSectionId}
            sectionId={selectedSectionId}
            mode={mode}
            clientId={clientId}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Settings2 className="h-12 w-12 mb-4 opacity-20" />
            <p className="text-sm font-medium">Select a category and section to start building</p>
            <p className="text-xs mt-1 opacity-70">Changes save automatically as you edit</p>
          </div>
        )}
      </div>
    </div>
  );
}
